// Wires the three generator-style tool widgets — the plain URL generator, the
// WiFi generator, and the vCard generator. They share the same preview +
// download chrome (pages/tools/_qr_result) and the same renderer (qr-core);
// only the payload string differs per `data-qr-widget` kind.
import { makeQr, toSvg, toPngDataUrl, downloadUrl, downloadText } from "./qr-core"
import { minPrintMm, formatPrintSize } from "./density"

const GENERATOR_KINDS = ["generator", "wifi", "vcard"]

function val(widget, selector) {
  const el = widget.querySelector(selector)
  return el ? el.value.trim() : ""
}

// WIFI: and vCard fields escape a few reserved characters.
function escWifi(s) {
  return s.replace(/([\\;,:"])/g, "\\$1")
}

// vCard 3.0 text values (RFC 2426 section 4): backslash, comma and semicolon
// get a backslash, a newline is written as \n, and any other control
// character is dropped. Without it "Reed Electric, LLC" split in two and a
// pasted newline could start a property of its own. Matches
// Tools::VcardPayload.escape, which the free MCP tool and the hosted contact
// page use; for text with none of these characters the output is unchanged.
function escVcard(s) {
  return s
    .replace(/\r\n?/g, "\n")
    .replace(/\\/g, "\\\\")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;")
    .replace(/\n/g, "\\n")
    .replace(/[\u0000-\u001F\u007F]/g, "")
}

// A URL is a URI value, not text, so it is not backslash-escaped, but it
// loses any control character for the same reason.
function cleanVcardUri(s) {
  return s.replace(/[\u0000-\u001F\u007F]/g, "")
}

function payloadFor(widget) {
  switch (widget.dataset.qrWidget) {
    case "generator":
      return val(widget, "[data-qr-input]")

    case "wifi": {
      const ssid = val(widget, "[data-wifi-ssid]")
      if (!ssid) return ""
      const enc = val(widget, "[data-wifi-enc]") || "WPA"
      const hidden = widget.querySelector("[data-wifi-hidden]")?.checked
      if (enc === "nopass") {
        return `WIFI:T:nopass;S:${escWifi(ssid)};${hidden ? "H:true;" : ""};`
      }
      const pass = val(widget, "[data-wifi-pass]")
      return `WIFI:T:${enc};S:${escWifi(ssid)};P:${escWifi(pass)};${hidden ? "H:true;" : ""};`
    }

    case "vcard": {
      const first = val(widget, "[data-vcard-first]")
      const last = val(widget, "[data-vcard-last]")
      if (!first && !last) return ""
      const phone = val(widget, "[data-vcard-phone]")
      const email = val(widget, "[data-vcard-email]")
      const org = val(widget, "[data-vcard-org]")
      const title = val(widget, "[data-vcard-title]")
      const url = val(widget, "[data-vcard-url]")
      const lines = [
        "BEGIN:VCARD",
        "VERSION:3.0",
        `N:${escVcard(last)};${escVcard(first)};;;`,
        `FN:${escVcard([first, last].filter(Boolean).join(" "))}`
      ]
      if (phone) lines.push(`TEL;TYPE=CELL:${escVcard(phone)}`)
      if (email) lines.push(`EMAIL:${escVcard(email)}`)
      if (org) lines.push(`ORG:${escVcard(org)}`)
      if (title) lines.push(`TITLE:${escVcard(title)}`)
      if (url) lines.push(`URL:${cleanVcardUri(url)}`)
      lines.push("END:VCARD")
      return lines.join("\r\n")
    }

    default:
      return ""
  }
}

function setupWidget(widget) {
  const result = widget.querySelector("[data-qr-result]")
  const empty = widget.querySelector("[data-qr-empty]")
  const preview = widget.querySelector("[data-qr-preview]")
  const pngBtn = widget.querySelector("[data-qr-download-png]")
  const svgBtn = widget.querySelector("[data-qr-download-svg]")
  const printBtn = widget.querySelector("[data-qr-print]")
  const printTemplate = widget.querySelector("[data-qr-print-template]")
  const sizeModules = widget.querySelector("[data-qr-size-modules]")
  const sizeMin = widget.querySelector("[data-qr-size-min]")
  const filename = widget.dataset.qrFilename || "qr-code"
  let current = null

  function showEmpty(message) {
    current = null
    if (result) result.hidden = true
    if (empty) {
      empty.hidden = false
      if (message) empty.textContent = message
    }
  }

  function render() {
    const text = payloadFor(widget)
    if (!text) return showEmpty()

    const fg = val(widget, "[data-qr-fg]") || "#111111"
    const bg = val(widget, "[data-qr-bg]") || "#ffffff"
    let qr
    try {
      qr = makeQr(text)
    } catch {
      return showEmpty("That's a bit too long to fit in a QR code. Try shortening it.")
    }

    preview.innerHTML = toSvg(qr, { fg, bg })
    const svgEl = preview.querySelector("svg")
    if (svgEl) {
      svgEl.classList.add("w-full", "h-full")
      svgEl.setAttribute("role", "img")
      svgEl.setAttribute("aria-label", "QR code preview")
    }
    // How dense the code is and the smallest width to print it. It changes with
    // every field, so the person sees what each one costs before the download.
    const count = qr.getModuleCount()
    if (sizeModules) sizeModules.textContent = `${count} by ${count}`
    if (sizeMin) sizeMin.textContent = formatPrintSize(minPrintMm(count))
    current = { qr, fg, bg }
    if (empty) empty.hidden = true
    if (result) result.hidden = false
  }

  widget.addEventListener("input", render)
  widget.addEventListener("change", render)

  pngBtn?.addEventListener("click", () => {
    if (!current) return
    downloadUrl(`${filename}.png`, toPngDataUrl(current.qr, { fg: current.fg, bg: current.bg }))
  })
  svgBtn?.addEventListener("click", () => {
    if (!current) return
    downloadText(`${filename}.svg`, toSvg(current.qr, { fg: current.fg, bg: current.bg }))
  })

  // "Print a card": the code with a title and a caption, alone on the page.
  // The card goes directly under <body> for the print so the stylesheet can
  // hide everything else (see application.css), and comes out when the print
  // dialog closes. Always black on white: it is going on paper.
  printBtn?.addEventListener("click", () => {
    if (!current || !printTemplate) return
    const root = document.createElement("div")
    root.dataset.qrPrintRoot = ""
    root.appendChild(printTemplate.content.cloneNode(true))
    const code = root.querySelector("[data-qr-print-code]")
    if (code) {
      code.innerHTML = toSvg(current.qr)
      code.querySelector("svg")?.setAttribute("width", "100%")
      code.querySelector("svg")?.setAttribute("height", "100%")
    }
    const caption = root.querySelector("[data-qr-print-caption]")
    if (caption) caption.textContent = val(widget, caption.dataset.qrPrintCaptionFrom)
    document.body.appendChild(root)
    document.documentElement.dataset.printCard = "true"
    window.addEventListener(
      "afterprint",
      () => {
        delete document.documentElement.dataset.printCard
        root.remove()
      },
      { once: true }
    )
    window.print()
  })

  render()
}

export function setupQrGenerators() {
  const selector = GENERATOR_KINDS.map((k) => `[data-qr-widget="${k}"]`).join(",")
  document.querySelectorAll(selector).forEach(setupWidget)
  setupContactHandover()
}

// "Make it editable" on the vCard tool (#332): carry what was typed to the
// homepage builder as a hosted contact page. sessionStorage, same origin, never
// the URL, and nothing is sent until the person presses Generate there; the
// builder reads the key once and removes it (QrBuilder's readContactDraft).
// The link only renders while hosted pages are on for everyone.
const CONTACT_DRAFT_KEY = "tq_contact_draft"

function setupContactHandover() {
  const widget = document.querySelector('[data-qr-widget="vcard"]')
  if (!widget) return
  document.querySelectorAll("[data-contact-handover]").forEach((link) => {
    link.addEventListener("click", () => {
      const draft = {
        first_name: val(widget, "[data-vcard-first]"),
        last_name: val(widget, "[data-vcard-last]"),
        mobile_phone: val(widget, "[data-vcard-phone]"),
        email: val(widget, "[data-vcard-email]"),
        business_name: val(widget, "[data-vcard-org]"),
        job_title: val(widget, "[data-vcard-title]"),
        website_url: val(widget, "[data-vcard-url]"),
      }
      try {
        window.sessionStorage.setItem(CONTACT_DRAFT_KEY, JSON.stringify(draft))
      } catch {
        // No storage (private mode, blocked site data): the builder opens
        // empty, which is still the right place to land.
      }
    })
  })
}
